import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { PrismLight as SyntaxHighlighter } from "react-syntax-highlighter";
import sql from "react-syntax-highlighter/dist/esm/languages/prism/sql";
import json from "react-syntax-highlighter/dist/esm/languages/prism/json";
import { oneLight } from "react-syntax-highlighter/dist/esm/styles/prism";
import { Check, Copy } from "lucide-react";
import { ContextUnavailableError, contextErrorMessage, type ProposalStatus, type TableKind } from "@/lib/contextApi";
import { Badge, type BadgeTone } from "@/components/ui";
import { cn } from "@/lib/utils";

SyntaxHighlighter.registerLanguage("sql", sql);
SyntaxHighlighter.registerLanguage("json", json);

// ---------------------------------------------------------------------------
// Page-level "service unreachable" reporting: sections never show their own offline error.
// ---------------------------------------------------------------------------

const UnavailableCtx = createContext<(() => void) | null>(null);
export const UnavailableProvider = UnavailableCtx.Provider;

/**
 * Returns a handler: for ContextUnavailableError it flags the whole page offline and returns null,
 * otherwise it returns the message to show inline.
 */
export function useErrorHandler() {
  const report = useContext(UnavailableCtx);
  return useCallback(
    (e: unknown): string | null => {
      if (e instanceof ContextUnavailableError) {
        report?.();
        return null;
      }
      return contextErrorMessage(e);
    },
    [report],
  );
}

// ---------------------------------------------------------------------------
// Formatting
// ---------------------------------------------------------------------------

export function formatDateTime(iso?: string | null): string {
  if (!iso) return "–";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("nl-NL", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

export function formatBytes(n?: number | null): string {
  if (n === undefined || n === null || !Number.isFinite(n)) return "–";
  const units = ["B", "KB", "MB", "GB", "TB", "PB"];
  let v = n;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  return `${v.toLocaleString("nl-NL", { maximumFractionDigits: v < 10 && i > 0 ? 1 : 0 })} ${units[i]}`;
}

export function formatUsd(n?: number | null): string {
  if (n === undefined || n === null || !Number.isFinite(n)) return "–";
  return `$${n.toLocaleString("en-US", { minimumFractionDigits: n < 0.01 ? 4 : 2, maximumFractionDigits: n < 0.01 ? 4 : 2 })}`;
}

/** Text form of a value for diffs: strings as-is, empty for null/undefined, JSON otherwise. */
export function valueToText(v: unknown): string {
  if (v === null || v === undefined) return "";
  if (typeof v === "string") return v;
  try {
    return JSON.stringify(v, null, 2);
  } catch {
    return String(v);
  }
}

export function useDebounced<T>(value: T, delay = 300): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

export function newSessionId(): string {
  const c = globalThis.crypto as Crypto | undefined;
  if (c && typeof c.randomUUID === "function") return c.randomUUID();
  if (c && typeof c.getRandomValues === "function") {
    const b = c.getRandomValues(new Uint8Array(16));
    b[6] = (b[6] & 0x0f) | 0x40;
    b[8] = (b[8] & 0x3f) | 0x80;
    const h = Array.from(b, (x) => x.toString(16).padStart(2, "0")).join("");
    return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
  }
  return `s-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

// ---------------------------------------------------------------------------
// Small visual pieces
// ---------------------------------------------------------------------------

const KIND_TONE: Record<string, BadgeTone> = { fact: "info", dim: "brand", other: "neutral" };

const KIND_LABEL: Record<string, string> = { fact: "Feit", dim: "Dimensie", other: "Overig" };
export const kindLabel = (k?: string | null) => KIND_LABEL[k || "other"] ?? (k || "Overig");

export function KindBadge({ kind, className }: { kind?: TableKind | string | null; className?: string }) {
  const k = kind || "other";
  return (
    <Badge tone={KIND_TONE[k] ?? "neutral"} className={cn("px-1.5 py-0 text-[11px]", className)} title={`Tabeltype: ${kindLabel(k)}`}>
      {kindLabel(k)}
    </Badge>
  );
}

export function QualityIndicator({ quality }: { quality: { tests_passed: number; tests_total: number } | null | undefined }) {
  if (!quality || !quality.tests_total) return null;
  const ratio = quality.tests_passed / quality.tests_total;
  const tone = ratio >= 1 ? "bg-success" : ratio >= 0.8 ? "bg-sl-yellow" : "bg-destructive";
  return (
    <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground" title={`${quality.tests_passed} van ${quality.tests_total} datatests geslaagd`}>
      <span className="h-1.5 w-12 overflow-hidden rounded-full bg-muted">
        <span className={cn("block h-full rounded-full", tone)} style={{ width: `${Math.round(ratio * 100)}%` }} />
      </span>
      <span className="tabular-nums">
        {quality.tests_passed}/{quality.tests_total}
      </span>
    </span>
  );
}

/** Copy-to-clipboard hook: `copied` flips true for 1.5 s after a successful copy. */
export function useCopy() {
  const [copied, setCopied] = useState(false);
  const copy = useCallback((text: string) => {
    navigator.clipboard?.writeText(text).then(
      () => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      },
      () => undefined,
    );
  }, []);
  return { copied, copy };
}

export function CopyButton({ text, label = "Kopiëren", className, iconOnly }: { text: string; label?: string; className?: string; iconOnly?: boolean }) {
  const { copied, copy } = useCopy();
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        copy(text);
      }}
      className={cn(
        "inline-flex h-7 shrink-0 items-center gap-1.5 rounded-md border border-border bg-card px-2 text-xs font-medium text-muted-foreground transition-colors hover:border-sl-grey-300 hover:text-foreground",
        iconOnly && "w-7 justify-center px-0",
        className,
      )}
      aria-label={copied ? "Gekopieerd" : label}
      title={copied ? "Gekopieerd" : label}
    >
      {copied ? <Check className="h-3.5 w-3.5 text-success" /> : <Copy className="h-3.5 w-3.5" />}
      {!iconOnly && <span>{copied ? "Gekopieerd" : label}</span>}
      <span className="sr-only" aria-live="polite">
        {copied ? "Gekopieerd naar klembord" : ""}
      </span>
    </button>
  );
}

export function CodeBlock({
  code,
  language = "sql",
  className,
  maxHeight = 360,
  title,
  copyLabel,
}: {
  code: string;
  language?: string;
  className?: string;
  maxHeight?: number;
  title?: ReactNode;
  copyLabel?: string;
}) {
  const [expanded, setExpanded] = useState(false);
  const lines = code.split("\n").length;
  const canExpand = lines * 19 > maxHeight;
  return (
    <div className={cn("overflow-hidden rounded-md border border-border bg-sl-grey-50", className)}>
      <div className="flex items-center justify-between gap-2 border-b border-border bg-card px-3 py-1.5">
        <span className="min-w-0 truncate text-xs font-medium text-muted-foreground">
          {title ?? <span className="font-mono uppercase tracking-wide">{language}</span>}
          <span className="ml-2 font-normal tabular-nums">· {lines} regel{lines === 1 ? "" : "s"}</span>
        </span>
        <div className="flex shrink-0 items-center gap-1.5">
          {canExpand && (
            <button
              type="button"
              onClick={() => setExpanded((x) => !x)}
              className="inline-flex h-7 items-center rounded-md px-2 text-xs font-medium text-muted-foreground hover:bg-muted hover:text-foreground"
              aria-expanded={expanded}
            >
              {expanded ? "Inklappen" : "Alles tonen"}
            </button>
          )}
          <CopyButton text={code} label={copyLabel ?? (language === "sql" ? "Kopieer SQL" : "Kopiëren")} />
        </div>
      </div>
      <SyntaxHighlighter
        language={language}
        style={oneLight}
        customStyle={{ margin: 0, padding: "0.75rem 1rem", background: "transparent", fontSize: "0.8125rem", lineHeight: 1.5, maxHeight: expanded ? undefined : maxHeight, overflow: "auto" }}
        codeTagProps={{ style: { fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" } }}
      >
        {code}
      </SyntaxHighlighter>
    </div>
  );
}

/** Plain text with `backtick` spans rendered as inline code (cheap alternative to full markdown). */
export function InlineText({ text, className }: { text: string; className?: string }) {
  const parts = text.split(/(`[^`]+`)/g);
  return (
    <span className={cn("whitespace-pre-wrap break-words", className)}>
      {parts.map((p, i) =>
        p.startsWith("`") && p.endsWith("`") && p.length > 2 ? (
          <code key={i} className="rounded bg-muted px-1 py-px font-mono text-[0.85em] text-foreground">
            {p.slice(1, -1)}
          </code>
        ) : (
          <span key={i}>{p}</span>
        ),
      )}
    </span>
  );
}

export function Markdown({ children, className }: { children: string; className?: string }) {
  return (
    <div className={cn("space-y-2 text-sm leading-relaxed break-words", className)}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          p: ({ children }) => <p className="my-1.5">{children}</p>,
          a: ({ children, href }) => (
            <a href={href} target="_blank" rel="noreferrer" className="font-medium underline underline-offset-2">
              {children}
            </a>
          ),
          ul: ({ children }) => <ul className="my-1.5 list-disc space-y-0.5 pl-5">{children}</ul>,
          ol: ({ children }) => <ol className="my-1.5 list-decimal space-y-0.5 pl-5">{children}</ol>,
          h1: ({ children }) => <h3 className="mt-3 text-base font-semibold">{children}</h3>,
          h2: ({ children }) => <h3 className="mt-3 text-base font-semibold">{children}</h3>,
          h3: ({ children }) => <h4 className="mt-2 text-sm font-semibold">{children}</h4>,
          h4: ({ children }) => <h4 className="mt-2 text-sm font-semibold">{children}</h4>,
          blockquote: ({ children }) => <blockquote className="border-l-2 border-sl-yellow pl-3 text-muted-foreground">{children}</blockquote>,
          table: ({ children }) => (
            <div className="my-2 overflow-x-auto rounded-md border border-border">
              <table className="w-full border-collapse text-xs">{children}</table>
            </div>
          ),
          th: ({ children }) => <th className="border-b border-border bg-sl-grey-50 px-2 py-1.5 text-left font-semibold">{children}</th>,
          td: ({ children }) => <td className="border-b border-border px-2 py-1.5 align-top">{children}</td>,
          pre: ({ children }) => <>{children}</>,
          code: ({ className, children }) => {
            const text = String(children ?? "").replace(/\n$/, "");
            const lang = /language-(\w+)/.exec(className || "")?.[1];
            if (lang || text.includes("\n")) return <CodeBlock code={text} language={lang === "json" ? "json" : "sql"} className="my-2" />;
            return <code className="rounded bg-muted px-1 py-0.5 font-mono text-[0.8em]">{children}</code>;
          },
        }}
      >
        {children}
      </ReactMarkdown>
    </div>
  );
}

export function SectionTitle({ children, icon, className }: { children: ReactNode; icon?: ReactNode; className?: string }) {
  return (
    <h4 className={cn("mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground", className)}>
      {icon}
      {children}
    </h4>
  );
}

const STATUS: Record<ProposalStatus, { label: string; tone: BadgeTone }> = {
  draft: { label: "Concept", tone: "neutral" },
  checking: { label: "Wordt gecontroleerd", tone: "info" },
  ready: { label: "Klaar voor review", tone: "brand" },
  published: { label: "Gepubliceerd", tone: "success" },
  rejected: { label: "Afgewezen", tone: "danger" },
};

export const PROPOSAL_STATUS_LABEL = (s: ProposalStatus) => STATUS[s]?.label ?? s;

export function StatusBadge({ status }: { status: ProposalStatus | string }) {
  const s = STATUS[status as ProposalStatus];
  return <Badge tone={s?.tone ?? "neutral"}>{s?.label ?? status}</Badge>;
}
